"use client";

import {
  PrizeWheel,
  type PrizeWheelRef,
} from "@/components/ui/custom/prize-wheel";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Action, useAnalytics } from "@/context/AnalyticsProvider";
import {
  DollarSign,
  Gamepad2,
  Gift,
  type LucideIcon,
  Shield,
} from "lucide-react";
import Image from "next/image";
import { useEffect, useMemo, useRef, useState } from "react";
import { PRIZE_IMAGE_MAP, mapTierToSegmentColour } from "./PrizeWheelUtils";
import type { PrizeWheelSegmentData } from "./types";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
};

const FEATURE_CARDS = [
  {
    title: "DISCOVER\nGAMES",
    icon: Gamepad2,
  },
  {
    title: "DO\nQUESTS",
    icon: Shield,
  },
  {
    title: "EARN\nREWARDS",
    icon: DollarSign,
  },
];

// Hardcoded segments for onboarding (no API call)
// Arranged to distribute high tier (1-2) and low tier (4-5) prizes evenly
const ONBOARDING_SEGMENTS: PrizeWheelSegmentData[] = [
  {
    id: "meta-quest",
    label: "Meta Quest 3",
    tier: mapTierToSegmentColour(1), // Yellow - Legendary
    imageUrl: PRIZE_IMAGE_MAP["meta quest 3"],
  },
  {
    id: "apple-card",
    label: "$20 Apple",
    tier: mapTierToSegmentColour(2), // Purple - Epic
    imageUrl: PRIZE_IMAGE_MAP["$20 apple"],
  },
  {
    id: "100-gems",
    label: "100 Gems",
    tier: mapTierToSegmentColour(4), // Green - Uncommon
    imageUrl: PRIZE_IMAGE_MAP["100 gems"],
  },
  {
    id: "10-gems",
    label: "10 Gems",
    tier: mapTierToSegmentColour(5), // Grey - Common
    imageUrl: PRIZE_IMAGE_MAP["10 gems"],
  },
  {
    id: "ps5",
    label: "PlayStation 5",
    tier: mapTierToSegmentColour(1), // Yellow - Legendary
    imageUrl: PRIZE_IMAGE_MAP["playstation 5"],
  },
  {
    id: "xbox-card",
    label: "$25 Xbox",
    tier: mapTierToSegmentColour(2), // Purple - Epic
    imageUrl: PRIZE_IMAGE_MAP["$25 xbox"],
  },
  {
    id: "1-imx",
    label: "1 IMX",
    tier: mapTierToSegmentColour(3), // Blue - Rare
    imageUrl: PRIZE_IMAGE_MAP["1 imx"],
  },
  {
    id: "50-gems",
    label: "50 Gems",
    tier: mapTierToSegmentColour(4), // Green - Uncommon
    imageUrl: PRIZE_IMAGE_MAP["50 gems"],
  },
  {
    id: "xbox-series-x",
    label: "Series X",
    tier: mapTierToSegmentColour(1), // Yellow - Legendary
    imageUrl: PRIZE_IMAGE_MAP["series x"],
  },
  {
    id: "5-gems",
    label: "5 Gems",
    tier: mapTierToSegmentColour(5), // Grey - Common
    imageUrl: PRIZE_IMAGE_MAP["5 gems"],
  },
  {
    id: "50-imx",
    label: "50 IMX",
    tier: mapTierToSegmentColour(2), // Purple - Epic
    imageUrl: PRIZE_IMAGE_MAP["50 imx"],
  },
  {
    id: "200-gems",
    label: "200 Gems",
    tier: mapTierToSegmentColour(4), // Green - Uncommon
    imageUrl: PRIZE_IMAGE_MAP["100 gems"], // Use the large gems image
  },
  {
    id: "switch-2",
    label: "Switch 2",
    tier: mapTierToSegmentColour(1), // Yellow - Legendary
    imageUrl: PRIZE_IMAGE_MAP["switch 2"],
  },
  {
    id: "10-imx",
    label: "10 IMX",
    tier: mapTierToSegmentColour(3), // Blue - Rare
    imageUrl: PRIZE_IMAGE_MAP["10 imx"],
  },
  {
    id: "ps-card",
    label: "$20 PlayStation",
    tier: mapTierToSegmentColour(2), // Purple - Epic
    imageUrl: PRIZE_IMAGE_MAP["$20 playstation"],
  },
  {
    id: "500-gems",
    label: "500 Gems",
    tier: mapTierToSegmentColour(4), // Green - Uncommon
    imageUrl: PRIZE_IMAGE_MAP["500 gems"],
  },
  {
    id: "5-imx",
    label: "5 IMX",
    tier: mapTierToSegmentColour(3), // Blue - Rare
    imageUrl: PRIZE_IMAGE_MAP["5 imx"],
  },
  {
    id: "20-gems",
    label: "20 Gems",
    tier: mapTierToSegmentColour(5), // Grey - Common
    imageUrl: PRIZE_IMAGE_MAP["20 gems"],
  },
];

const FeatureCard: React.FC<{
  title: string;
  icon: LucideIcon;
  onClick?: () => void;
}> = ({ title, icon: Icon, onClick }) => {
  return (
    <div
      className="relative flex-1 aspect-square rounded-3xl overflow-hidden cursor-pointer"
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      // biome-ignore lint/a11y/useSemanticElements: Need to maintain flex layout properties that don't work well with button element
      role="button"
      tabIndex={0}
      aria-label={title.replace("\n", " ")}
    >
      {/* Background image */}
      <Image
        src="/images/onboarding/feature-card-background.webp"
        alt=""
        fill
        className="object-cover opacity-80"
      />

      {/* Content */}
      <div className="absolute inset-1 flex flex-col items-center justify-center gap-1 p-2 rounded-3xl border-[3px] border-white/10">
        <p className="text-center font-bold uppercase text-[8px] leading-tight tracking-wider whitespace-pre-line">
          {title}
        </p>
        <Icon className="text-white flex-shrink-0 w-4 h-4" />
      </div>
    </div>
  );
};

export function OnboardingPrizeWheelModal({
  open,
  onOpenChange,
  onComplete,
}: Props) {
  const { trackV3 } = useAnalytics();
  const wheelRefDesktop = useRef<PrizeWheelRef>(null);
  const wheelRefMobile = useRef<PrizeWheelRef>(null);
  const spinStateRef = useRef<{
    expectedId: string | null;
    reported: boolean;
    completed: { desktop: boolean; mobile: boolean };
  }>({
    expectedId: null,
    reported: false,
    completed: { desktop: false, mobile: false },
  });
  const [isSpinning, setIsSpinning] = useState(false);
  const [mode, setMode] = useState<"wheel" | "prize">("wheel");
  const [winning, setWinning] = useState<PrizeWheelSegmentData | null>(null);
  const prizeDelayRef = useRef<number | null>(null);
  const [spinsCompleted, setSpinsCompleted] = useState(0);
  const [overlayKey, setOverlayKey] = useState(0);
  const [videoReady, setVideoReady] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  const TOTAL_SPINS = 1;
  const spinsRemaining = TOTAL_SPINS - spinsCompleted;

  const handleModalClose = (open: boolean) => {
    // CRITICAL: Prevent closing the modal during onboarding flow
    // The modal should ONLY close when login is successful (handled by parent component)
    // User interactions (clicking outside, etc.) should NOT close the modal
    if (!open) {
      // Don't call onOpenChange - prevent modal from closing
      return;
    }
    onOpenChange(open);
  };

  const showOverlay =
    isSpinning || mode === "prize" || (mode === "wheel" && winning !== null);

  const wheelUiHidden = isSpinning || (mode === "wheel" && winning !== null);
  const bodyFadeClass = useMemo(
    () =>
      `transition-opacity duration-500 ${wheelUiHidden ? "opacity-0 pointer-events-none" : "opacity-100"}`,
    [wheelUiHidden],
  );

  useEffect(() => {
    if (!showOverlay) return;
    setVideoReady(false);
    setOverlayKey((k) => k + 1);
  }, [showOverlay]);

  // Reset when modal opens
  useEffect(() => {
    if (!open) return;
    setMode("wheel");
    setWinning(null);
    setIsSpinning(false);
    setSpinsCompleted(0);
    spinStateRef.current.expectedId = null;
    spinStateRef.current.reported = false;
    spinStateRef.current.completed = { desktop: false, mobile: false };
    if (prizeDelayRef.current) {
      clearTimeout(prizeDelayRef.current);
      prizeDelayRef.current = null;
    }
  }, [open]);

  const handleWheelSpinningChange = (spinning: boolean) => {
    if (spinning) setIsSpinning(true);
  };

  const handleWheelComplete = (
    source: "desktop" | "mobile",
    seg: PrizeWheelSegmentData,
  ) => {
    if (spinStateRef.current.reported) return;

    if (
      spinStateRef.current.expectedId &&
      seg.id !== spinStateRef.current.expectedId
    ) {
      return;
    }

    spinStateRef.current.completed[source] = true;
    const bothDone =
      spinStateRef.current.completed.desktop &&
      spinStateRef.current.completed.mobile;

    if (!bothDone) return;

    spinStateRef.current.reported = true;
    setIsSpinning(false);
    setWinning(seg);

    // Increment spins completed after this spin
    setSpinsCompleted((prev) => prev + 1);

    if (prizeDelayRef.current) {
      clearTimeout(prizeDelayRef.current);
    }

    prizeDelayRef.current = window.setTimeout(() => {
      setMode("prize");
      prizeDelayRef.current = null;
    }, 300);
  };

  const handleSpinPrimary = () => {
    // Track spin event when button is clicked (before spin starts)
    trackV3({
      object: "Prize_Wheel",
      action: Action.PRESSED,
      extras: {
        isLoggedIn: false,
        spinNumber: spinsCompleted + 1,
      },
    });

    // Single spin lands on 200 gems (no API call)
    const targetId = "200-gems";
    const targetSegment = ONBOARDING_SEGMENTS.find((s) => s.id === targetId);
    if (!targetSegment) return;

    // Mark as started on first click - wheel will mount and then spin
    if (!hasStarted) {
      setHasStarted(true);
      // Wait for wheel to mount before spinning
      setTimeout(() => {
        setIsSpinning(true);
        spinStateRef.current.expectedId = targetSegment.id;
        spinStateRef.current.reported = false;
        spinStateRef.current.completed = { desktop: false, mobile: false };
        wheelRefDesktop.current?.spin(targetSegment.id);
        wheelRefMobile.current?.spin(targetSegment.id);
      }, 100);
    } else {
      // Wheel already exists, spin immediately
      setIsSpinning(true);
      spinStateRef.current.expectedId = targetSegment.id;
      spinStateRef.current.reported = false;
      spinStateRef.current.completed = { desktop: false, mobile: false };
      wheelRefDesktop.current?.spin(targetSegment.id);
      wheelRefMobile.current?.spin(targetSegment.id);
    }
  };

  const handleSpinAgain = () => {
    setOverlayKey((k) => k + 1);
    setWinning(null);
    setMode("wheel");

    // Wait for mode change and re-render before spinning
    setTimeout(() => {
      handleSpinPrimary();
    }, 100);
  };

  const handleFinish = () => {
    onComplete();
  };

  return (
    <Dialog open={open} onOpenChange={handleModalClose} modal={false}>
      <DialogContent className="max-w-[95vw] w-screen md:w-[95vw] h-[95vh] p-0 md:p-0 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 fixed border border-white/10 bg-background/95 shadow-none md:shadow-lg rounded-2xl overflow-hidden [&>button]:hidden">
        <div className="sticky top-- z-30 w-full h-[88px] flex flex-col">
          <div className="flex-1 flex items-center px-5">
            <div className="flex items-center gap-5">
              <div className="relative w-12 h-12 rounded-[4px] bg-[#131313] flex items-center justify-center">
                <img
                  src="/icons/favicon-onLight-512x512.png"
                  alt="Immutable"
                  className="w-6 h-6 object-contain"
                />
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 rounded-[4px] border border-white/10"
                />
              </div>
              <DialogTitle className="text-xs font-bold text-neutral-50">
                Prize Wheel
              </DialogTitle>
            </div>
          </div>
          <div aria-hidden className="w-full h-px bg-white/10 flex-shrink-0" />
        </div>
        <div
          className={`absolute inset-x-0 top-[88px] bottom-0 z-0 pointer-events-none isolate transition-opacity duration-300 ${showOverlay ? "opacity-100" : "opacity-0"}`}
        >
          {/* Video background when spinning */}
          {isSpinning && (
            <video
              className="absolute inset-0 w-full h-full object-cover"
              src={`/video/prize-wheel/spin-prize-overlay.mp4?v=${overlayKey}`}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              key={overlayKey}
              onLoadedData={(e) => {
                setVideoReady(true);
                const vid = e.currentTarget as HTMLVideoElement;
                if (isSpinning && vid.isConnected) {
                  try {
                    vid.currentTime = 0;
                    void vid.play();
                  } catch {}
                }
              }}
            />
          )}
          {/* Static image background when showing prize */}
          {mode === "prize" && (
            <img
              className="absolute top-[30%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] h-[90%] min-[667px]:w-[120%] min-[667px]:h-[120%] md:w-[70%] md:h-[70%] object-contain"
              src="/assets/prize-wheel/onboarding-wheel-prize-bg.webp"
              alt="Prize background"
            />
          )}
        </div>

        <div
          className="flex flex-col items-center justify-start gap-2 w-full h-full min-h-[80vh] relative pb-6 px-4 md:px-6 overflow-y-auto overflow-x-hidden"
          style={{
            paddingTop: mode === "wheel" ? "0px" : "24px",
          }}
        >
          {mode === "wheel" ? (
            <>
              {/* Static Wheel Image - only show before first button click */}
              {!hasStarted && (
                <div className="w-full flex-1 flex justify-center items-center relative z-10 min-h-0">
                  <img
                    src="/assets/prize-wheel/wheel-onboarding.webp"
                    alt="Prize Wheel"
                    className="w-auto h-auto max-w-full max-h-full object-contain"
                    style={{ transform: "scale(1.75)" }}
                  />
                </div>
              )}

              {/* Desktop wheel - show after first button click */}
              {hasStarted && (
                <div
                  className={`w-full flex-1 flex justify-center items-center relative z-10 hidden md:flex min-h-0 outline-none mt-8 ${
                    !isSpinning && spinsRemaining > 0
                      ? "cursor-pointer"
                      : "cursor-not-allowed"
                  }`}
                  onClick={() => {
                    if (!isSpinning && spinsRemaining > 0) {
                      handleSpinPrimary();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      if (!isSpinning && spinsRemaining > 0) {
                        handleSpinPrimary();
                      }
                    }
                  }}
                  // biome-ignore lint/a11y/useSemanticElements: Need to maintain flex layout properties that don't work well with button element
                  role="button"
                  tabIndex={0}
                  aria-label="Spin the prize wheel"
                  aria-disabled={isSpinning || spinsRemaining <= 0}
                >
                  <PrizeWheel
                    ref={wheelRefDesktop}
                    segments={ONBOARDING_SEGMENTS}
                    size="xl"
                    className="flex-shrink-0"
                    vwScale={0.82}
                    vhScale={0.62}
                    onSpinComplete={(seg) =>
                      handleWheelComplete("desktop", seg)
                    }
                    onSpinningChange={handleWheelSpinningChange}
                  />
                </div>
              )}
              {/* Mobile wheel - show after first button click */}
              {hasStarted && (
                <div
                  className={`w-full flex-1 relative z-10 md:hidden outline-none ${
                    !isSpinning && spinsRemaining > 0
                      ? "cursor-pointer"
                      : "cursor-not-allowed"
                  }`}
                  onClick={() => {
                    if (!isSpinning && spinsRemaining > 0) {
                      handleSpinPrimary();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      if (!isSpinning && spinsRemaining > 0) {
                        handleSpinPrimary();
                      }
                    }
                  }}
                  // biome-ignore lint/a11y/useSemanticElements: Need to maintain flex layout properties that don't work well with button element
                  role="button"
                  tabIndex={0}
                  aria-label="Spin the prize wheel"
                  aria-disabled={isSpinning || spinsRemaining <= 0}
                >
                  <div
                    className="absolute left-0 top-1/2"
                    style={{
                      transform: "translateX(-50%) translateY(-50%) scale(2)",
                      transformOrigin: "center",
                      WebkitMaskImage:
                        isSpinning || winning
                          ? "linear-gradient(to right, transparent 0%, rgba(0,0,0,1) 50%, rgba(0,0,0,1) 100%)"
                          : "linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.1) 15%, rgba(0,0,0,1) 30%, rgba(0,0,0,1) 70%, rgba(0,0,0,0.1) 85%, rgba(0,0,0,0.05) 100%), linear-gradient(to right, transparent 0%, rgba(0,0,0,1) 50%, rgba(0,0,0,1) 100%)",
                      maskImage:
                        isSpinning || winning
                          ? "linear-gradient(to right, transparent 0%, rgba(0,0,0,1) 50%, rgba(0,0,0,1) 100%)"
                          : "linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.1) 15%, rgba(0,0,0,1) 30%, rgba(0,0,0,1) 70%, rgba(0,0,0,0.1) 85%, rgba(0,0,0,0.05) 100%), linear-gradient(to right, transparent 0%, rgba(0,0,0,1) 50%, rgba(0,0,0,1) 100%)",
                      WebkitMaskComposite: "intersect",
                      maskComposite: "intersect",
                    }}
                  >
                    <PrizeWheel
                      ref={wheelRefMobile}
                      segments={ONBOARDING_SEGMENTS}
                      size="xl"
                      className="flex-shrink-0 max-w-[200vw] max-h-[120vh] w-auto h-auto"
                      onSpinComplete={(seg) =>
                        handleWheelComplete("mobile", seg)
                      }
                      onSpinningChange={handleWheelSpinningChange}
                    />
                  </div>
                </div>
              )}
              {/* Logo, Badge and Button - only show before first button click */}
              {!hasStarted && (
                <div className="flex flex-col items-center gap-5 w-full px-6 md:px-0 relative z-30 flex-shrink-0 mt-4">
                  {/* Immutable Play Logo - Pixel Perfect from Figma */}
                  <div className="h-12 flex items-center justify-center relative shrink-0 -mt-4">
                    <div className="h-12 w-[249px] relative overflow-clip">
                      {/* Immutable Symbol - Left side (0% to 19.28% of 249px) */}
                      <div className="absolute left-0 top-0 bottom-0 right-[80.72%] overflow-clip">
                        <img
                          src="/icons/favicon-onDark-512x512.png"
                          alt="Immutable"
                          className="block max-w-none w-full h-full object-contain"
                        />
                      </div>
                      {/* "Immutable Play" Text - Right side */}
                      <div className="absolute left-[19.28%] right-0 top-0 bottom-0 flex items-center pl-2">
                        <span className="text-white text-2xl leading-[48px] whitespace-nowrap">
                          <span className="font-bold">Immutable</span> Play
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Gift From A Friend Badge */}
                  <div className="flex items-center gap-2 px-4 py-2 rounded-md">
                    <Gift className="h-5 w-5 text-white" />
                    <span className="text-base font-semibold text-white whitespace-nowrap leading-6">
                      Gift From A Friend
                    </span>
                    <Gift className="h-5 w-5 text-white" />
                  </div>

                  {/* Claim Spins Button */}
                  <div className="-mt-4">
                    <button
                      onClick={handleSpinPrimary}
                      disabled={isSpinning || spinsRemaining <= 0}
                      className="bg-[#f7f7f7] text-[#0d0d0d] flex items-center justify-center gap-2 px-4 py-2 rounded-full shadow-[0px_1px_3px_0px_rgba(0,0,0,0.16),0px_1px_3px_0px_rgba(0,0,0,0.32)] disabled:opacity-50 disabled:cursor-not-allowed h-12"
                      type="button"
                    >
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <img
                          src="/assets/icons/pinwheel.svg"
                          alt="Spin icon"
                          className="w-4 h-4 object-contain brightness-0"
                        />
                      </div>
                      <span className="font-medium text-nowrap flex-shrink-0 text-sm leading-5">
                        {isSpinning ? "Spinning..." : "Try the wheel"}
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Title */}
              <div
                className={`text-center z-10 -mt-6 ${isSpinning ? "opacity-0 transition-opacity duration-500" : "opacity-100"}`}
              >
                <h2 className="text-2xl font-bold leading-8 text-neutral-50 text-center">
                  Sign Up to Claim Rare Prize
                  <br />A Bag Of 200 Gems
                </h2>
              </div>

              {/* Prize Image */}
              <div className="w-full flex justify-center items-center relative z-10 mt-2">
                <img
                  src="/icons/prize-wheel/large-gems.webp"
                  alt="200 Gems"
                  className="w-[200px] h-[200px] min-[667px]:w-[260px] min-[667px]:h-[260px] md:w-[240px] md:h-[240px] object-contain"
                />
              </div>

              {/* Sign Up Button */}
              <div
                className={`flex flex-col items-center gap-2.5 z-30 mt-2 ${isSpinning ? "opacity-0 transition-opacity duration-500" : "opacity-100"}`}
              >
                <button
                  onClick={handleFinish}
                  type="button"
                  className="bg-[#f7f7f7] text-[#0d0d0d] h-10 md:h-12 px-3 md:px-4 py-2 rounded-full shadow-[0px_1px_3px_0px_rgba(0,0,0,0.16),0px_1px_3px_0px_rgba(0,0,0,0.32)] flex items-center gap-2"
                >
                  <Gift className="w-3 h-3 md:w-4 md:h-4 text-[#0d0d0d]" />
                  <span className="text-xs md:text-sm font-medium leading-4 md:leading-5 whitespace-nowrap">
                    Sign Up to Claim
                  </span>
                </button>
              </div>

              {/* Subtitle */}
              <div
                className={`text-center z-10 mt-4 [@media(max-height:667px)]:mt-2 ${isSpinning ? "opacity-0 transition-opacity duration-500" : "opacity-100"}`}
              >
                <p className="text-xs md:text-sm font-medium leading-4 md:leading-5 text-neutral-50 text-center">
                  100,000 players are winning:
                </p>
              </div>

              {/* High Value Prizes Row */}
              <div
                className={`flex gap-2 md:gap-2.5 lg:gap-4 w-full justify-center px-4 z-10 mt-3 [@media(max-height:667px)]:mt-2 ${isSpinning ? "opacity-0 transition-opacity duration-500" : "opacity-100"}`}
              >
                <div className="flex flex-col gap-1 md:gap-2 items-center">
                  <div className="w-[45px] h-[45px] md:w-[60px] md:h-[60px] overflow-hidden">
                    <img
                      src="/icons/prize-wheel/meta-quest.webp"
                      alt="Quest 3"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <p className="text-xs md:text-sm leading-4 md:leading-5 text-neutral-50 text-center">
                    Quest 3
                  </p>
                </div>
                <div className="flex flex-col gap-1 md:gap-2 items-center">
                  <div className="w-[45px] h-[45px] md:w-[60px] md:h-[60px] overflow-hidden">
                    <img
                      src="/icons/prize-wheel/hardware-xbox.webp"
                      alt="XBOX"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <p className="text-xs md:text-sm leading-4 md:leading-5 text-neutral-50 text-center">
                    XBOX
                  </p>
                </div>
                <div className="flex flex-col gap-1 md:gap-2 items-center">
                  <div className="w-[45px] h-[45px] md:w-[60px] md:h-[60px] overflow-hidden">
                    <img
                      src="/icons/prize-wheel/hardware-ps5.webp"
                      alt="PS5"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <p className="text-xs md:text-sm leading-4 md:leading-5 text-neutral-50 text-center">
                    PS5
                  </p>
                </div>
                <div className="flex flex-col gap-1 md:gap-2 items-center">
                  <div className="w-[45px] h-[45px] md:w-[60px] md:h-[60px] overflow-hidden">
                    <img
                      src="/icons/prize-wheel/hardware-switch.webp"
                      alt="Switch 2"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <p className="text-xs md:text-sm leading-4 md:leading-5 text-neutral-50 text-center">
                    Switch 2
                  </p>
                </div>
              </div>

              {/* Action Cards */}
              <div className="flex flex-row gap-2 lg:gap-3 w-full justify-center px-8 lg:max-w-sm max-w-xs mt-1 [@media(max-height:667px)]:mt-1 [@media(max-height:667px)]:scale-90">
                {FEATURE_CARDS.map((card) => (
                  <FeatureCard
                    key={card.title}
                    title={card.title}
                    icon={card.icon}
                    onClick={handleFinish}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default OnboardingPrizeWheelModal;
